import { z } from "zod";
import { countWorkingDays, moment } from "../../date";
import { localized } from "../../locales";
import { ZodDateString } from "../zod-types/zod-date-string";
import { ZodProjectState } from "../zod-types/zod-project-state";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";

export const ticketSchema = z
	.object({
		projectId: z.string().nonempty(), // foreign key
		type: z.string().nonempty(), // foreign key
		description: z.string().min(1),
		state: ZodProjectState,
		note: ZodStringNullable,

		dateMode: z.enum(["date", "week"]).nullish(),
		/** Both ends are mandatory: `tickets.start_date` and `tickets.end_date` are NOT NULL. */
		startDate: ZodDateString,
		endDate: ZodDateString,
		/**
		 * The work inside the window, which is not the same as the window's length. Mandatory:
		 * `tickets.estimated_days` is NOT NULL, and a bar with no length is not a plan.
		 */
		estimatedDays: z.coerce.number().int().min(1),
		/**
		 * The weekdays the ticket's project works, so the estimate's ceiling is counted in the site's
		 * own week rather than in a Monday-to-Friday one.
		 *
		 * Context, not a column: the form fills it from the project it is opened on and nothing
		 * writes it. The handler clamps against the stored project regardless, because a body could
		 * claim any week it liked.
		 */
		projectWeekdays: z.array(z.number().int().min(0).max(6)).optional(),
		assignee: ZodStringNullable,
	})
	.transform((value) => {
		// Dates entered the wrong way round are swapped rather than refused: the two inputs are a
		// span, and which one the user filled first is not information worth an error. A one-day
		// ticket keeps both ends on the same day — collapsing it to a single date is what left the
		// column half-filled for as long as it was nullable.
		if (moment(value.startDate).isAfter(value.endDate)) {
			const _startDate = value.startDate;
			value.startDate = value.endDate;
			value.endDate = _startDate;
		}
		return value;
	})
	.superRefine((value, context) => {
		// The estimate counts working days, so its ceiling is the working days the window holds —
		// not the calendar days between its ends. A Friday-to-Sunday window is three days long and
		// holds one, and an estimate of three there is work the board could not draw. The check runs
		// after the transform, so the span it measures is the one that will be stored.
		// Floored at one, because the estimate is mandatory and a window holding no working day at
		// all — a Saturday to a Sunday — would otherwise have a ceiling of zero and no satisfiable
		// answer.
		const available = Math.max(
			1,
			countWorkingDays(value.startDate, value.endDate, value.projectWeekdays),
		);
		if (value.estimatedDays <= available) return;

		context.addIssue({
			code: "custom",
			path: ["estimatedDays"],
			message: localized("projects.estimated_days_exceeds_span", {
				count: String(available),
			}),
		});
	});

export type TicketFormSchema = z.infer<typeof ticketSchema>;
